/**
 * MAHI — "what are you shopping for?", the first and only thing between a
 * visitor and their first card.
 *
 * Two taps wide on purpose. Every other question the app used to ask at the
 * door — name, birth year, address, height, a full-body photo — has moved to
 * the moment the feature that needs it is reached. This one stayed because it
 * is the one answer that changes every card rather than one screen: the live
 * catalogue holds 45,613 womenswear products against 5,380 menswear, so
 * guessing wrong does not make the feed slightly less relevant, it makes it
 * entirely wrong for half the people who arrive.
 *
 * Not a modal over the feed, unlike PreferenceSetup. There is nothing
 * underneath worth showing yet — the deck behind it would be a deck we are
 * about to throw away and refetch — so this is a full screen that hands
 * straight over to cards.
 *
 * "Everything" is a first-class third answer and not a skip link. Someone who
 * genuinely wants the whole catalogue should be able to say so in the same
 * tap as everyone else, and calling it "skip" would misfile a real preference
 * as a refusal to answer.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { errMsg, trackEvent } from '../../api';
import MahiLogo from '../ui/MahiLogo';

const CHOICES = [
  {
    key: 'womenswear',
    label: 'Womenswear',
    hint: 'Sarees, lehengas, dresses, everyday',
    // Coral through lavender, the brand's own gradient rather than a neutral
    // card — this is the first surface anyone sees and it should look like
    // MAHI rather than like a settings screen.
    art: 'from-raspberry-400 via-raspberry-300 to-[#B9A8F0]',
  },
  {
    key: 'menswear',
    label: 'Menswear',
    hint: 'Kurtas, shirts, sherwanis, denim',
    art: 'from-mahiteal-400 via-mahiteal-300 to-[#8FD2F0]',
  },
];

export default function ShoppingForSetup({ onDone }) {
  const [saving, setSaving] = useState('');

  const choose = async (key) => {
    if (saving) return;
    setSaving(key);
    try {
      await mahi.put('/profile/shopping-for', { shopping_for: key });
      trackEvent('shopping_for_set', { shopping_for: key });
      onDone(key);
    } catch (e) {
      // Never dismissed on failure. Carrying on with an unsaved answer would
      // drop them into a feed filtered the other way with no explanation, and
      // re-asking on the next load would look like the tap did nothing.
      toast.error(errMsg(e, 'Could not save that - try again?'));
      setSaving('');
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] bg-blush-100 flex flex-col items-center justify-center px-5 py-8 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      role="dialog"
      aria-modal="true"
      aria-label="What are you shopping for?"
      data-testid="shopping-for-setup"
    >
      <MahiLogo className="w-24 h-auto text-slate-800 mb-7 shrink-0" decorative />

      {/* MAHI's own voice — mahiteal, serif, italic, the same treatment
          onboarding and the preference picker give her lines, so the first
          thing anyone reads is her asking rather than a form appearing. */}
      <p className="text-sm text-mahiteal-600 font-mahi-serif italic text-center mb-3 px-2">
        &ldquo;One thing and then I&apos;ll get out of your way.&rdquo;
      </p>
      <h1 className="font-mahi-serif text-[28px] leading-tight text-slate-900 text-center">
        What are you shopping for?
      </h1>
      <p className="text-[13px] text-slate-500 font-mahi-sans text-center mt-2 mb-7">
        You can change this any time.
      </p>

      <div className="w-full max-w-md grid grid-cols-2 gap-3">
        {CHOICES.map((choice, i) => (
          <motion.button
            key={choice.key}
            type="button"
            disabled={Boolean(saving)}
            onClick={() => choose(choice.key)}
            data-testid={`shopping-for-${choice.key}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 * i, type: 'spring', stiffness: 300, damping: 26 }}
            whileTap={{ scale: 0.97 }}
            className="group relative overflow-hidden rounded-3xl bg-white text-left shadow-sm ring-1 ring-slate-900/5 disabled:opacity-60 transition"
          >
            <div className={`h-28 sm:h-32 bg-gradient-to-br ${choice.art}`} aria-hidden="true">
              {/* A soft flowing shape rather than a hard block — the brand's
                  visual language, and it keeps the two cards from reading as
                  swatches. */}
              <svg viewBox="0 0 200 120" className="w-full h-full opacity-40" preserveAspectRatio="none">
                <path d="M0 78 C 48 34, 92 104, 138 62 S 186 22, 200 40 L200 120 L0 120 Z"
                      fill="white" fillOpacity="0.45" />
              </svg>
            </div>
            <div className="px-4 py-3.5">
              <div className="flex items-center gap-1.5">
                <span className="font-mahi-sans font-medium text-[15px] text-slate-900">
                  {choice.label}
                </span>
                {saving === choice.key && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                )}
              </div>
              <p className="text-[11.5px] text-slate-500 font-mahi-sans mt-0.5 leading-snug">
                {choice.hint}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <button
        type="button"
        disabled={Boolean(saving)}
        onClick={() => choose('everything')}
        data-testid="shopping-for-everything"
        className="mt-5 inline-flex items-center gap-2 text-[13px] text-slate-600 hover:text-slate-900 font-mahi-sans underline underline-offset-4 decoration-slate-300 py-2 disabled:opacity-50"
      >
        {saving === 'everything' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Show me everything
      </button>

      <p className="mt-6 text-[13px] text-slate-500 font-mahi-sans">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-slate-900 hover:text-raspberry-600 transition">
          Sign in
        </Link>
      </p>
    </motion.div>
  );
}
